import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Receipt, Upload, CheckCircle2, XCircle, Wallet,
  Clock, Filter, Eye, Trash2, Settings as SettingsIcon, Send,
  ArrowDownCircle, ArrowUpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const CATEGORIES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'food', label: 'Food' },
  { value: 'transport', label: 'Transport' },
  { value: 'tools', label: 'Tools' },
  { value: 'office', label: 'Office Supplies' },
  { value: 'other', label: 'Other' },
];

const STATUS_META = {
  submitted:  { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  approved:   { label: 'Approved',         color: 'bg-blue-100 text-blue-700' },
  rejected:   { label: 'Rejected',         color: 'bg-red-100 text-red-700' },
  reimbursed: { label: 'Reimbursed',       color: 'bg-emerald-100 text-emerald-700' },
};

const emptyForm = {
  amount: '',
  currency: 'SAR',
  category: 'fuel',
  description: '',
  expense_date: new Date().toISOString().slice(0, 10),
  vendor_name: '',
  receipt_base64: '',
  receipt_filename: '',
};

const money = (n, cur = 'SAR') => `${cur} ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PettyCashPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin';
  const isFinance = role === 'finance';
  const isSales = role === 'sales';
  const isOperations = role === 'operations';
  const canSendAdvance = isAdmin || isOperations;
  const canApprove = isAdmin || isOperations;

  const [bills, setBills] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({ per_bill_limit: null, require_receipt: true });
  const [loading, setLoading] = useState(true);

  // Balance + advances
  const [balance, setBalance] = useState({ available_balance: 0, advances_received: 0, expenses_deducted: 0 });
  const [advances, setAdvances] = useState([]);
  const [users, setUsers] = useState([]);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({
    receiver_id: '', amount: '', transfer_method: 'bank_transfer', reference: '', note: '',
  });
  const [sendingAdvance, setSendingAdvance] = useState(false);
  const [activeTab, setActiveTab] = useState('bills'); // bills | advances

  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reimburseOpen, setReimburseOpen] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [reimburseData, setReimburseData] = useState({ payment_method: 'bank_transfer', reference: '', note: '' });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({ per_bill_limit: '', require_receipt: true });

  const loadAll = async () => {
    try {
      setLoading(true);
      // Load core data first — these are safe for any role.
      const [billsRes, statsRes, settingsRes, balanceRes, advancesRes] = await Promise.all([
        api.get('/petty-cash', { params: statusFilter !== 'all' ? { status: statusFilter } : {} }),
        api.get('/petty-cash/stats'),
        api.get('/petty-cash/settings'),
        api.get('/petty-cash/balance'),
        api.get('/petty-cash/advances'),
      ]);
      setBills(billsRes.data || []);
      setStats(statsRes.data || {});
      setSettings(settingsRes.data || {});
      setBalance(balanceRes.data || {});
      setAdvances(advancesRes.data || []);
      // Only Admin/Operations need the recipient list. Load it in a separate
      // try/catch so a 403 (or any error) can NEVER crash the page or log the
      // user out. Uses the dedicated /petty-cash/eligible-recipients endpoint
      // which is Admin+Operations accessible (unlike /admin/users which is
      // Admin-only and would boot Operations users out).
      if (canSendAdvance) {
        try {
          const r = await api.get('/petty-cash/eligible-recipients');
          setUsers((r.data || []).filter((u) => u.id !== user?.id));
        } catch (err) {
          console.warn('Failed to load recipients — Send Advance will be disabled', err);
          setUsers([]);
          if (err.response?.status !== 403) {
            toast.error('Could not load advance recipients');
          }
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load petty cash');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAdvance = async () => {
    if (!advanceForm.receiver_id) { toast.error('Select a recipient'); return; }
    if (!advanceForm.amount || Number(advanceForm.amount) <= 0) { toast.error('Enter an amount'); return; }
    setSendingAdvance(true);
    try {
      await api.post('/petty-cash/advances', {
        ...advanceForm,
        amount: Number(advanceForm.amount),
      });
      toast.success('Advance sent');
      setAdvanceOpen(false);
      setAdvanceForm({ receiver_id: '', amount: '', transfer_method: 'bank_transfer', reference: '', note: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send advance');
    } finally {
      setSendingAdvance(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, receipt_base64: reader.result, receipt_filename: file.name }));
      setUploading(false);
    };
    reader.onerror = () => { setUploading(false); toast.error('Failed to read file'); };
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (settings.require_receipt && !form.receipt_base64) {
      toast.error('Receipt attachment is required');
      return;
    }
    try {
      await api.post('/petty-cash', { ...form, amount: Number(form.amount) });
      toast.success('Bill submitted');
      setCreateOpen(false);
      setForm(emptyForm);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(`/petty-cash/${selected.id}/approve`, { note: approveNote || null });
      toast.success('Bill approved');
      setApproveOpen(false); setApproveNote(''); setViewOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason is required'); return; }
    try {
      await api.post(`/petty-cash/${selected.id}/reject`, { reason: rejectReason });
      toast.success('Bill rejected');
      setRejectOpen(false); setRejectReason(''); setViewOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleReimburse = async () => {
    try {
      await api.post(`/petty-cash/${selected.id}/reimburse`, reimburseData);
      toast.success('Marked as reimbursed');
      setReimburseOpen(false); setViewOpen(false);
      setReimburseData({ payment_method: 'bank_transfer', reference: '', note: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (bill) => {
    if (!window.confirm('Delete this bill?')) return;
    try {
      await api.delete(`/petty-cash/${bill.id}`);
      toast.success('Deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openSettings = () => {
    setSettingsDraft({
      per_bill_limit: settings.per_bill_limit || '',
      require_receipt: settings.require_receipt !== false,
    });
    setSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/petty-cash/settings', {
        per_bill_limit: settingsDraft.per_bill_limit === '' ? null : Number(settingsDraft.per_bill_limit),
        require_receipt: settingsDraft.require_receipt,
      });
      toast.success('Settings updated');
      setSettingsOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const isImage = (r) => r && r.startsWith('data:image');
  const isPdf = (r) => r && (r.startsWith('data:application/pdf') || r.includes('application/pdf'));

  return (
    <div className="space-y-6" data-testid="petty-cash-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <Wallet className="h-8 w-8" /> Petty Cash
          </h1>
          <p className="text-muted-foreground mt-1">
            {isSales ? 'Submit expenses with a receipt photo. Track approvals & reimbursements.' :
              'Review submitted bills, approve or reject, and mark reimbursements.'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button variant="outline" onClick={openSettings} data-testid="petty-cash-settings-button">
              <SettingsIcon className="h-4 w-4 mr-2" /> Settings
            </Button>
          )}
          {canSendAdvance && (
            <Button
              variant="outline"
              onClick={() => setAdvanceOpen(true)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="send-advance-button"
            >
              <Send className="h-4 w-4 mr-2" /> Send Advance
            </Button>
          )}
          <Button onClick={() => { setForm(emptyForm); setCreateOpen(true); }} data-testid="new-bill-button">
            <Plus className="h-4 w-4 mr-2" /> New Bill
          </Button>
        </div>
      </div>

      {/* My Balance card */}
      <Card className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200" data-testid="petty-cash-balance-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-800 font-semibold">Your Available Balance</p>
            <p className="text-3xl sm:text-4xl font-black text-emerald-900 mt-1" data-testid="available-balance-value">
              {money(balance.available_balance, 'SAR')}
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Advances: {money(balance.advances_received)} · Deducted: {money(balance.expenses_deducted)}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge
              variant={activeTab === 'bills' ? 'default' : 'outline'}
              className="cursor-pointer text-xs px-3 py-1.5"
              onClick={() => setActiveTab('bills')}
              data-testid="tab-bills"
            >
              Expenses
            </Badge>
            <Badge
              variant={activeTab === 'advances' ? 'default' : 'outline'}
              className="cursor-pointer text-xs px-3 py-1.5"
              onClick={() => setActiveTab('advances')}
              data-testid="tab-advances"
            >
              Advances {advances.length > 0 && `(${advances.length})`}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Clock className="h-5 w-5" />} label="Pending Approval" value={stats.submitted_count || 0} sub={money(stats.pending_approval_amount)} tone="amber" />
        <StatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Awaiting Reimbursement" value={stats.approved_count || 0} sub={money(stats.pending_reimbursement_amount)} tone="blue" />
        <StatCard icon={<Wallet className="h-5 w-5" />} label="Reimbursed" value={stats.reimbursed_count || 0} sub={money(stats.reimbursed_amount)} tone="emerald" />
        <StatCard icon={<XCircle className="h-5 w-5" />} label="Rejected" value={stats.rejected_count || 0} sub="" tone="red" />
      </div>

      {/* Filters (only for bills tab) */}
      {activeTab === 'bills' && (
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="submitted">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved (awaiting reimbursement)</SelectItem>
              <SelectItem value="reimbursed">Reimbursed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Bills List */}
      {activeTab === 'bills' && (
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : bills.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Receipt className="mx-auto h-12 w-12 mb-2 opacity-40" />
              No petty cash bills yet.
            </div>
          ) : (
            <div className="divide-y">
              {bills.map((b) => {
                const s = STATUS_META[b.status] || {};
                return (
                  <div key={b.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between hover:bg-slate-50" data-testid={`bill-row-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{money(b.amount, b.currency)}</p>
                        <Badge className={s.color}>{s.label}</Badge>
                        <Badge variant="outline">{b.category}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 truncate">{b.description}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {b.submitted_by_name} • {b.expense_date} • {b.vendor_name || 'no vendor'}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => { setSelected(b); setViewOpen(true); }} data-testid={`view-bill-${b.id}`}>
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                      {(b.submitted_by === user?.id && (b.status === 'submitted' || b.status === 'rejected')) && (
                        <Button variant="outline" size="sm" onClick={() => handleDelete(b)} data-testid={`delete-bill-${b.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Advances List */}
      {activeTab === 'advances' && (
        <Card data-testid="advances-list">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : advances.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Send className="mx-auto h-12 w-12 mb-2 opacity-40" />
                No advances yet.
              </div>
            ) : (
              <div className="divide-y">
                {advances.map((a) => {
                  const isReceived = a.receiver_id === user?.id;
                  return (
                    <div key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between hover:bg-slate-50" data-testid={`advance-row-${a.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {isReceived ? (
                          <ArrowDownCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                        ) : (
                          <ArrowUpCircle className="h-5 w-5 text-amber-600 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{money(a.amount, a.currency)}</p>
                            <Badge className={isReceived ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                              {isReceived ? `From ${a.sender_name}` : `To ${a.receiver_name}`}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {(a.transfer_method || '').replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {a.reference && <>Ref: {a.reference} · </>}
                            {a.note || 'No note'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(a.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      {/* Send Advance dialog */}
      <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
        <DialogContent className="max-w-md" data-testid="send-advance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" /> Send Petty Cash Advance
            </DialogTitle>
            <DialogDescription>
              Transfer petty cash to a user. Their available balance updates immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Recipient</Label>
              <Select value={advanceForm.receiver_id} onValueChange={(v) => setAdvanceForm({ ...advanceForm, receiver_id: v })}>
                <SelectTrigger data-testid="advance-recipient-select"><SelectValue placeholder={users.length ? 'Select user' : 'No recipients available'} /></SelectTrigger>
                <SelectContent>
                  {users.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">No eligible recipients loaded</div>
                  )}
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || u.email} · {u.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (SAR)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={advanceForm.amount}
                onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                data-testid="advance-amount-input"
              />
            </div>
            <div>
              <Label>Transfer Method *</Label>
              <Select value={advanceForm.transfer_method} onValueChange={(v) => setAdvanceForm({ ...advanceForm, transfer_method: v })}>
                <SelectTrigger data-testid="advance-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference (optional)</Label>
              <Input
                value={advanceForm.reference}
                onChange={(e) => setAdvanceForm({ ...advanceForm, reference: e.target.value })}
                placeholder="Bank ref no. / receipt no."
                data-testid="advance-reference-input"
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                value={advanceForm.note}
                onChange={(e) => setAdvanceForm({ ...advanceForm, note: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceOpen(false)}>Cancel</Button>
            <Button onClick={handleSendAdvance} disabled={sendingAdvance} data-testid="advance-submit-button">
              {sendingAdvance ? 'Sending...' : 'Send Advance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-bill-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit Petty Cash Bill</DialogTitle>
            <DialogDescription>
              Attach the receipt photo. Bill will go to Admin for approval, then Finance for reimbursement.
              {settings.per_bill_limit ? ` Per-bill limit: ${money(settings.per_bill_limit)}.` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="bill-amount-input"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="bill-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAR">SAR</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="bill-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expense date</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                  data-testid="bill-date-input"
                />
              </div>
            </div>
            <div>
              <Label>Vendor / Merchant (optional)</Label>
              <Input
                value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                placeholder="e.g. ADNOC petrol station"
                data-testid="bill-vendor-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What was this spent on?"
                data-testid="bill-description-input"
              />
            </div>
            <div>
              <Label>Receipt {settings.require_receipt ? '(required)' : '(optional)'}</Label>
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-receipt-button"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Reading...' : form.receipt_filename || 'Choose photo or PDF'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="receipt-file-input"
                />
              </div>
              {form.receipt_base64 && isImage(form.receipt_base64) && (
                <img src={form.receipt_base64} alt="receipt preview" className="mt-2 max-h-40 rounded border" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="submit-bill-button">Submit Bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent data-testid="view-bill-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Petty Cash Bill</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold">{money(selected.amount, selected.currency)}</p>
                <Badge className={STATUS_META[selected.status]?.color}>{STATUS_META[selected.status]?.label}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Category" value={selected.category} />
                <Info label="Expense date" value={selected.expense_date} />
                <Info label="Submitted by" value={selected.submitted_by_name} />
                <Info label="Submitted at" value={selected.submitted_at?.slice(0, 16).replace('T', ' ')} />
                {selected.vendor_name && <Info label="Vendor" value={selected.vendor_name} />}
                {selected.approved_by_name && <Info label={selected.status === 'rejected' ? 'Rejected by' : 'Approved by'} value={selected.approved_by_name} />}
                {selected.reimbursed_by_name && <Info label="Reimbursed by" value={selected.reimbursed_by_name} />}
                {selected.payment_method && <Info label="Paid via" value={selected.payment_method} />}
                {selected.payment_reference && <Info label="Reference" value={selected.payment_reference} />}
              </div>
              <div>
                <p className="text-sm font-semibold">Description</p>
                <p className="text-sm mt-1">{selected.description}</p>
              </div>
              {selected.rejection_reason && (
                <div className="p-2 bg-red-50 rounded">
                  <p className="text-sm font-semibold text-red-700">Rejection reason</p>
                  <p className="text-sm text-red-700">{selected.rejection_reason}</p>
                </div>
              )}
              {selected.approval_note && (
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-sm font-semibold text-blue-700">Approval note</p>
                  <p className="text-sm text-blue-700">{selected.approval_note}</p>
                </div>
              )}
              {selected.receipt_base64 && (
                <div>
                  <p className="text-sm font-semibold mb-1">Receipt</p>
                  {isImage(selected.receipt_base64) ? (
                    <img src={selected.receipt_base64} alt="receipt" className="max-h-96 rounded border" />
                  ) : (
                    <a href={selected.receipt_base64} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">
                      Open attached file
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {selected?.status === 'submitted' && isAdmin && (
              <>
                <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="reject-bill-button">
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button onClick={() => setApproveOpen(true)} data-testid="approve-bill-button">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                </Button>
              </>
            )}
            {selected?.status === 'approved' && (isFinance || isAdmin) && (
              <Button onClick={() => setReimburseOpen(true)} data-testid="reimburse-bill-button">
                <Wallet className="h-4 w-4 mr-1" /> Mark Reimbursed
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve confirm */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve Bill</DialogTitle>
            <DialogDescription>Optional note that Finance will see.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={approveNote}
            onChange={(e) => setApproveNote(e.target.value)}
            placeholder="e.g. Approved – reimburse via next payroll"
            data-testid="approve-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} data-testid="confirm-approve-button">Confirm Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject confirm */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Bill</DialogTitle>
            <DialogDescription>Give a reason – the submitter will see this.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Receipt unreadable, please re-submit"
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-button">Confirm Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reimburse confirm */}
      <Dialog open={reimburseOpen} onOpenChange={setReimburseOpen}>
        <DialogContent data-testid="reimburse-dialog">
          <DialogHeader>
            <DialogTitle>Mark Reimbursed</DialogTitle>
            <DialogDescription>Record how this bill was paid back.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment method</Label>
              <Select value={reimburseData.payment_method} onValueChange={(v) => setReimburseData({ ...reimburseData, payment_method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="payroll">Payroll Adjustment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference # (optional)</Label>
              <Input
                value={reimburseData.reference}
                onChange={(e) => setReimburseData({ ...reimburseData, reference: e.target.value })}
                data-testid="reimburse-reference-input"
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                value={reimburseData.note}
                onChange={(e) => setReimburseData({ ...reimburseData, note: e.target.value })}
                data-testid="reimburse-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReimburseOpen(false)}>Cancel</Button>
            <Button onClick={handleReimburse} data-testid="confirm-reimburse-button">Mark Reimbursed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Petty Cash Settings</DialogTitle>
            <DialogDescription>Applies to all submissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Per-bill limit (SAR)</Label>
              <Input
                type="number"
                step="0.01"
                value={settingsDraft.per_bill_limit}
                onChange={(e) => setSettingsDraft({ ...settingsDraft, per_bill_limit: e.target.value })}
                placeholder="Leave empty for no limit"
                data-testid="settings-limit-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Bills above this amount will be rejected on submit.</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="require-receipt"
                type="checkbox"
                checked={settingsDraft.require_receipt}
                onChange={(e) => setSettingsDraft({ ...settingsDraft, require_receipt: e.target.checked })}
                data-testid="settings-require-receipt"
              />
              <Label htmlFor="require-receipt" className="mb-0">Require receipt attachment</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} data-testid="save-settings-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, tone }) => {
  const bg = {
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
  }[tone] || 'bg-slate-50 text-slate-700';
  return (
    <Card>
      <CardContent className="p-4">
        <div className={`inline-flex items-center gap-2 px-2 py-1 rounded ${bg} text-xs font-semibold`}>
          {icon} {label}
        </div>
        <p className="text-2xl font-bold mt-2">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium">{value || '—'}</p>
  </div>
);

export default PettyCashPage;
